/**
 * The per-player "poster" on the Final Results: one card per player summarising their game — rounds won, points,
 * their own star pattern and most combo win, and any of the game's records they hold.
 *
 * Pure and UI-agnostic, like the rest of `src/game/`: everything here is read off `game.rounds`, the ranking from
 * `summarise()` and the record holders from `findSessionRecords()` (the same source the Final Results badges use),
 * so a poster can never disagree with the table or the badges beside it. Nothing here scores a hand. The host and
 * every guest build the same posters from the same round history.
 */

import type { PlayerId } from '../rules/types';
import { findSessionRecords, type GameSummary } from './summary';
import type { Game, RoundRecord } from './types';

/** A round's outcome for one player, for the lines on their poster. */
export interface PosterRound {
  /** 1-based round number, as shown everywhere else ("round #3"). */
  round: number;
  /** This player's net points in that round. */
  points: number;
}

export interface PosterStar extends PosterRound {
  label: string;
  fan: number;
  limit: boolean;
}

export interface PosterStack extends PosterRound {
  /** How many distinct patterns landed on the one hand. */
  count: number;
  labels: string[];
}

export interface PosterBiggestWin extends PosterRound {
  fan: number;
  limit: boolean;
}

export interface PosterDuration extends PosterRound {
  durationMs: number;
}

export type RemarkKind = 'winner' | 'co-winner' | 'runner-up' | 'no-wins' | 'well-played';

export interface PosterRemark {
  kind: RemarkKind;
  /** A single emoji the card shows beside the remark; the trophy is only ever the winner's. */
  icon: string;
  text: string;
}

export interface PlayerPoster {
  player: PlayerId;
  name: string;
  rank: number;
  isWinner: boolean;
  /** Total points for the game — the same number as the ranking table. */
  points: number;
  roundsWon: number;
  roundsPlayed: number;
  /** This player's best single pattern across the hands they won. Absent when they won nothing (or no hand fired a pattern). */
  star?: PosterStar;
  /** This player's win with the most combos (distinct patterns on one hand). Absent under the same conditions. */
  stack?: PosterStack;
  /** Only for the player who holds the game's biggest win. */
  biggestWin?: PosterBiggestWin;
  /** Only for the player who holds the game's longest / fastest winning round. */
  longestRound?: PosterDuration;
  fastestRound?: PosterDuration;
  remark: PosterRemark;
}

/** What the card says to this player. The winner gets the trophy; everyone else gets something encouraging. */
export function remarkFor(rank: number, tiedAtTop: boolean, roundsWon: number): PosterRemark {
  if (rank === 1) {
    return tiedAtTop
      ? { kind: 'co-winner', icon: '🏆', text: 'Co-winner! You share first place in this game.' }
      : { kind: 'winner', icon: '🏆', text: "Congratulations — you're the winner of this game!" };
  }
  if (roundsWon === 0) return { kind: 'no-wins', icon: '🍀', text: 'No winning hand this time — the next game is yours!' };
  if (rank === 2) return { kind: 'runner-up', icon: '🥈', text: 'So close — runner-up. Well played!' };
  return { kind: 'well-played', icon: '🀄', text: 'Good game — thanks for playing!' };
}

const pointsIn = (round: RoundRecord, player: PlayerId): number =>
  round.payments.find((p) => p.player === player)?.delta ?? 0;

/**
 * One poster per player, best rank first (seat order breaks ties, as in `summarise`). Empty for a game with no
 * rounds played — every player would tie at nothing, and there is nothing to summarise.
 */
export function buildPlayerPosters(game: Game, summary: GameSummary): PlayerPoster[] {
  if (summary.roundsPlayed === 0) return [];
  const records = findSessionRecords(game);
  const tiedAtTop = summary.winners.length > 1;

  return summary.players.map((p): PlayerPoster => {
    const won = game.rounds.filter((r) => r.outcome === 'win' && r.winner === p.player);

    // Their single best pattern: the highest-fan pattern across every hand they won; on a tie the earliest wins.
    let star: PosterStar | undefined;
    // Their most combo win: the most distinct patterns (combos) on one hand; on a tie the earliest wins.
    let stack: PosterStack | undefined;
    for (const r of won) {
      const patterns = r.patterns ?? [];
      for (const pattern of patterns) {
        if (!star || pattern.fan > star.fan) {
          star = { label: pattern.label, fan: pattern.fan, limit: pattern.limit === true, round: r.index, points: pointsIn(r, p.player) };
        }
      }
      if (patterns.length > 0 && (!stack || patterns.length > stack.count)) {
        stack = { count: patterns.length, labels: patterns.map((x) => x.label), round: r.index, points: pointsIn(r, p.player) };
      }
    }

    const poster: PlayerPoster = {
      player: p.player,
      name: p.name,
      rank: p.rank,
      isWinner: p.rank === 1,
      points: p.totalScore,
      roundsWon: p.roundsWon,
      roundsPlayed: summary.roundsPlayed,
      remark: remarkFor(p.rank, tiedAtTop, p.roundsWon),
      ...(star ? { star } : {}),
      ...(stack ? { stack } : {}),
    };

    // The three records: each appears only on the poster of the player who holds it.
    const { biggestWin, longest, fastest } = records;
    if (biggestWin && biggestWin.winner === p.player) {
      poster.biggestWin = {
        fan: biggestWin.effectiveFan,
        limit: biggestWin.isLimit === true,
        round: biggestWin.index,
        points: pointsIn(biggestWin, p.player),
      };
    }
    if (longest && longest.round.winner === p.player) {
      poster.longestRound = { durationMs: longest.durationMs, round: longest.round.index, points: pointsIn(longest.round, p.player) };
    }
    if (fastest && fastest.round.winner === p.player) {
      poster.fastestRound = { durationMs: fastest.durationMs, round: fastest.round.index, points: pointsIn(fastest.round, p.player) };
    }
    return poster;
  });
}
